import type {
  Changeset,
  File as ChangesetFile,
  Error as ProtocolError,
  Message,
  Mode,
  Outcome,
  Part,
  Plan,
  RunState,
  RunStatus,
  StepStatus,
} from "./protocol";

/**
 * Turning `messages[]`/`parts[]` into a NARRATIVE (contract §2.5, §8.1; `Part.type`'s 11 values).
 *
 * WHY THIS MODULE EXISTS AT ALL. The reducer builds `messages[]` faithfully and nothing rendered
 * them: the conversation on the v1 path showed the user's question and no answer, because
 * `ChatPanel` reads the legacy store and the legacy stream is deliberately not subscribed when the
 * flag is on. Every protocol surface was stacked above an empty transcript, which is exactly why the
 * screen read as an execution dashboard rather than a conversation.
 *
 * THE CONTRACT ALREADY DESIGNED THE DISCLOSURE. `Part.type` is not a flat list of renderables — it
 * separates prose (`text`), thinking (`reasoning`), work (`tool_call`), and REFERENCES to things
 * with their own surface (`plan`, `changeset`, `deploy`, `evidence`, `diff`, `artifact`). Those
 * carry `ref` plus `meta`, which the schema describes as "display metadata for the part header, e.g.
 * {title}". So the intended shape is: a compact referring card in the transcript, detail elsewhere.
 * None of this is invented and none of it needs a backend change — it needed implementing.
 *
 * EVERYTHING HERE IS PURE. This repo has no `renderHook` and no `@testing-library`, so the rules
 * that decide what a reader sees live here where a node test can hold them, exactly as
 * `retrySubset.ts` and `usageMeter.ts` do.
 */

// ── Blocks ──────────────────────────────────────────────────────────────────

/**
 * One renderable block of an assistant turn.
 *
 * CONSECUTIVE TOOL CALLS COLLAPSE INTO ONE `activity` BLOCK, and that is the single biggest
 * quietening move available: a read run emits a dozen `tool.call_*` sequences, and a dozen rows of
 * "called graph_lookup" is the transcript-as-log problem in miniature. One line that says what
 * happened, with the detail behind it, carries the same information at a tenth of the height.
 */
export type TranscriptBlock =
  | { kind: "prose"; parts: Part[] }
  | { kind: "reasoning"; parts: Part[] }
  | { kind: "activity"; parts: Part[] }
  | { kind: "reference"; part: Part }
  | { kind: "error"; part: Part };

const REFERENCE_TYPES = new Set<Part["type"]>([
  "plan",
  "changeset",
  "deploy",
  "evidence",
  "diff",
  "artifact",
]);

/** Where a reference part's detail lives. Aligned to the workspace's sections. */
export type ReferenceTarget = "plan" | "changeset" | "deploy" | "evidence" | "artifact";

/** What a transcript block can ask to open: a reference target or the tool-activity list. The host decides what (if anything) opens. */
export type OpenableSection = ReferenceTarget | "activity";

/**
 * Where a transcript opener goes on a Lane V2 session. A document opens the artifact viewer; the run-keyed
 * "changeset" / "deploy" targets (file chips, source chips, diff previews) land on the newest task's section, which
 * shows the items and their diffs; "plan", "evidence" and "activity" have no surface, so they open nothing.
 */
export function workspaceOpenFor(
  section: OpenableSection,
  tasks: ReadonlyArray<{ task_id: string; created_at: string }>,
): { kind: "artifact" } | { kind: "task"; taskId: string } | null {
  if (section === "artifact") return { kind: "artifact" };
  if (section !== "changeset" && section !== "deploy") return null;
  let newest: { task_id: string; created_at: string } | null = null;
  for (const t of tasks) if (!newest || t.created_at > newest.created_at) newest = t;
  return newest ? { kind: "task", taskId: newest.task_id } : null;
}

/**
 * Group a message's parts into blocks, in `index` order.
 *
 * Order is the backend's: `Part.index` is authoritative and the reducer already inserts by it, so
 * this sorts defensively rather than trusting array position — a snapshot's parts arrive as a plain
 * array and nothing promises they were built in order.
 *
 * A part with no renderable content is DROPPED rather than rendered empty: a `text` part that has
 * streamed nothing yet would otherwise paint an empty paragraph that reflows when the first delta
 * lands.
 */
export function groupParts(
  parts: readonly Part[],
  /**
   * Reference part types the CALLER is already showing somewhere better.
   *
   * The transcript suppresses `plan` (the plan summary carries it, with the step titles a card
   * cannot) and `artifact` (the outputs list carries it). Without this, a run that emits a plan part
   * and two artifact parts renders the plan twice and every document twice — the duplication the
   * "nothing is duplicated" check exists to catch. It is a parameter rather than a constant because
   * the suppression is only valid while the better surface is actually on screen: the transcript
   * passes `plan` only when a plan summary rendered, so a plan part with no `RunState.plan` still
   * gets its card.
   */
  suppress?: ReadonlySet<Part["type"]>,
): TranscriptBlock[] {
  return groupOrdered([...parts].sort((a, b) => a.index - b.index), suppress);
}

/**
 * Every assistant message in one turn, as ONE block flow.
 *
 * WHY THIS IS NOT `groupParts(turn.assistant.flatMap(…))`. `groupParts` sorts by `index`, and
 * `index` is scoped to its MESSAGE — so a flat sort interleaves message 2's part 0 with message 1's
 * part 0 and shuffles the answer. Message order is the contract's own order; `index` orders only
 * within one.
 *
 * WHAT IT FIXES. Grouping ran per message, and the adjacency merge that turns a burst of tool calls
 * into one line only ever saw one message at a time. A run that emitted reasoning in two messages
 * rendered TWO "Thinking" disclosures stacked on each other, and two bursts of tool calls rendered
 * two "Used N tools" lines — seen in the browser. The reader is looking at one answer, so the run
 * that gets collapsed has to span the whole answer.
 */
export function groupTurnParts(
  messages: readonly Message[],
  suppress?: ReadonlySet<Part["type"]>,
): TranscriptBlock[] {
  const ordered = messages.flatMap((message) =>
    [...(message.parts ?? [])].sort((a, b) => a.index - b.index),
  );
  return groupOrdered(ordered, suppress);
}

/** The grouping itself, over parts ALREADY in render order. */
function groupOrdered(
  ordered: readonly Part[],
  suppress?: ReadonlySet<Part["type"]>,
): TranscriptBlock[] {
  const blocks: TranscriptBlock[] = [];

  for (const part of ordered) {
    if (part.type === "error") {
      blocks.push({ kind: "error", part });
      continue;
    }
    if (REFERENCE_TYPES.has(part.type)) {
      if (!suppress?.has(part.type)) blocks.push({ kind: "reference", part });
      continue;
    }
    const kind =
      part.type === "tool_call" ? "activity" : part.type === "reasoning" ? "reasoning" : "prose";

    // `citation` parts have no body of their own worth a block; they ride the prose they annotate.
    if (part.type === "citation") continue;
    if (kind === "prose" && !partText(part).trim()) continue;
    if (kind === "reasoning" && !partText(part).trim()) continue;

    const last = blocks[blocks.length - 1];
    if (last && last.kind === kind) {
      last.parts.push(part);
      continue;
    }
    blocks.push({ kind, parts: [part] } as TranscriptBlock);
  }
  return blocks;
}

/** A `text`/`reasoning` part's accumulated text. `body.text` is where the reducer puts deltas. */
export function partText(part: Part): string {
  const body = (part.body ?? {}) as { text?: unknown };
  return typeof body.text === "string" ? body.text : "";
}

/** Joined prose for a run of `text` parts, so one markdown render covers the block. */
export function proseOf(parts: readonly Part[]): string {
  return parts.map(partText).join("");
}

// ── Tool activity ───────────────────────────────────────────────────────────

export interface ToolLine {
  /** `display_name` when the backend sent one — it exists to be shown. */
  label: string;
  /** `ev.tool.call_result.summary`, which the schema types as a STRING. */
  summary: string | null;
  /** `duration_ms`, formatted, or null when the call has not finished. */
  duration: string | null;
  state: "running" | "ok" | "failed";
}

/**
 * One line per tool call, in the backend's own words.
 *
 * `display_name` and `category` come from `ev.tool.call_start`, and `summary`/`duration_ms`/`ok`
 * from `ev.tool.call_result` — all folded into the part's body by `reduceTool`. Falling back to
 * `name` and then to a fixed word means a call still reads as something rather than as an empty row;
 * the raw tool `name` is a function identifier, so it is the fallback and not the first choice.
 */
export function toolLine(part: Part): ToolLine {
  const body = (part.body ?? {}) as Record<string, unknown>;
  const str = (key: string) => (typeof body[key] === "string" ? (body[key] as string).trim() : "");
  const label = str("display_name") || str("name") || "Tool call";
  const summary = str("summary");
  const ms = typeof body.duration_ms === "number" ? body.duration_ms : null;
  const failed = body.ok === false || part.state === "error";
  return {
    label,
    summary: summary || null,
    duration: ms === null ? null : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`,
    state: failed ? "failed" : part.state === "streaming" ? "running" : "ok",
  };
}

/**
 * The one-line headline for a collapsed run of tool calls.
 *
 * Counts rather than names, because naming twelve tools in one line is the log this block exists to
 * replace. A failure is called out in the headline: a reader who never opens the disclosure must
 * still learn that something in there went wrong.
 */
export function activityHeadline(parts: readonly Part[]): string {
  const lines = parts.map(toolLine);
  const failed = lines.filter((l) => l.state === "failed").length;
  const running = lines.some((l) => l.state === "running");
  const n = lines.length;
  /*
   * "TOOLS", NOT "STEPS". This said `3 steps`, one line above a plan summary that says "I can make
   * this change in 2 steps" — the same word for two unrelated things, in the same conversation, and
   * an invitation to read the tool count as plan progress. Seen side by side in a browser.
   *
   * `tool` is not protocol vocabulary in the way `part` or `seq` is: every assistant product on the
   * market shows a reader that a tool ran, and it cannot be confused with anything else here.
   */
  const noun = `${n} tool call${n === 1 ? "" : "s"}`;
  if (running) return n === 1 ? `Working — ${lines[0].label}` : `Working — ${noun}`;
  if (failed) return `${noun} · ${failed} failed`;
  return noun;
}

/** One group of the evidence accordion: the calls that share a backend `category`. */
export interface ActivityGroup {
  /** The backend's `category` ("metadata", "graph", "tests"), or `null` for calls that carry none. */
  category: string | null;
  /** The heading: the category with its first letter raised, or "Tool calls" for the uncategorised. */
  label: string;
  parts: Part[];
}

/**
 * Tool calls grouped by the backend's `category`, in first-seen order.
 *
 * WHY GROUP AT ALL. Nine calls in one list read as a log; nine calls under three headings —
 * "Metadata · 5", "Searches · 3", "Tests · 1" — read as what the run looked at, which is the question
 * an evidence panel answers. `category` is the backend's own word for the axis (`mappers/tool.py`
 * sets it from the tool's stage), so nothing here invents a taxonomy: a producer that sends none gets
 * one group with a neutral heading, never a guessed one.
 */
export function activityGroups(parts: readonly Part[]): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  for (const part of parts) {
    const body = (part.body ?? {}) as { category?: unknown };
    const category = typeof body.category === "string" && body.category.trim() ? body.category.trim() : null;
    let group = groups.find((g) => g.category === category);
    if (!group) {
      group = {
        category,
        label: category ? category.charAt(0).toUpperCase() + category.slice(1) : "Tool calls",
        parts: [],
      };
      groups.push(group);
    }
    group.parts.push(part);
  }
  return groups;
}

/** "5 metadata · 3 graph · 1 tests" — the breakdown beside the accordion's headline. */
export function activityBreakdown(groups: readonly ActivityGroup[]): string | null {
  // One group carries nothing the headline's count does not.
  if (groups.length < 2) return null;
  return groups.map((g) => `${g.parts.length} ${g.label.toLowerCase()}`).join(" · ");
}

// ── Reference cards ─────────────────────────────────────────────────────────

export interface ReferenceCard {
  target: ReferenceTarget;
  /** `meta.title` where the producer sent one, else a derived label. Never an id. */
  title: string;
  /** The verb on the affordance — what opening it lets the reader do. */
  cta: string;
  /** `Part.ref` — the id of the thing referred to. Never rendered; used to open it. */
  ref: string | null;
}

const REFERENCE_DEFAULTS: Record<ReferenceTarget, { title: string; cta: string }> = {
  plan: { title: "Plan", cta: "Review plan" },
  changeset: { title: "Changes", cta: "Review changes" },
  deploy: { title: "Deployment", cta: "View deployment" },
  evidence: { title: "Evidence", cta: "View evidence" },
  artifact: { title: "Document", cta: "Open" },
};

/**
 * A reference part as a card.
 *
 * `diff` maps to `changeset` deliberately: a diff belongs to a file inside the changeset board, and
 * giving it a sixth workspace section would split one subject across two surfaces.
 *
 * `meta.title` is read defensively. The schema types `meta` as an open object and only DESCRIBES
 * `{title}` by example, so it is documentation-level rather than schema-pinned — a producer that
 * omits it is not violating anything, and the default has to carry the card.
 */
export function referenceCard(part: Part): ReferenceCard | null {
  const target: ReferenceTarget | null =
    part.type === "plan"
      ? "plan"
      : part.type === "changeset" || part.type === "diff"
        ? "changeset"
        : part.type === "deploy"
          ? "deploy"
          : part.type === "evidence"
            ? "evidence"
            : part.type === "artifact"
              ? "artifact"
              : null;
  if (!target) return null;
  const meta = (part.meta ?? {}) as { title?: unknown };
  const defaults = REFERENCE_DEFAULTS[target];
  const title = typeof meta.title === "string" && meta.title.trim() ? meta.title.trim() : defaults.title;
  return { target, title, cta: defaults.cta, ref: part.ref ?? null };
}

// ── The closing line ────────────────────────────────────────────────────────

/**
 * How the turn ENDED, in one sentence.
 *
 * DERIVED FROM `outcome`, NOT FROM `summary`. `ev.lifecycle.run_finished` does carry a `summary`,
 * but the schema types it `object | null` with NO properties defined — so there is no key this
 * client may read without inventing one, and rendering an unknown object is the raw-JSON dump the
 * brief rules out. `Outcome` is a closed 12-value enum and `completed_with_gaps` is a boolean, and
 * between them they say everything a closing line needs. (A pinned `summary` shape is a P2 backend
 * request; until then this is the honest version, not a placeholder.)
 *
 * Returns `null` while the run is live: a sentence about how it ended is a lie before it has.
 */
export function closingLine(run: RunState["run"] | null): { text: string; tone: "ok" | "warn" | "bad" } | null {
  if (!run) return null;
  const terminal = run.status === "completed" || run.status === "failed" || run.status === "cancelled";
  if (!terminal) return null;

  // The gaps flag OUTRANKS a converged outcome: §2.6's `completed_with_gaps` exists precisely
  // because "finished" and "finished with everything asked for" are different, and collapsing them
  // is the one thing it must not do.
  // if (run.completed_with_gaps) {
  //   return { text: "Finished, but not everything was completed.", tone: "warn" };
  // }

  /*
   * A PLAIN SUCCESS SAYS NOTHING, SO IT SAYS NOTHING.
   *
   * `converged` used to close the answer with the word "Done." — under a header already showing a
   * green dot and the word Done, at the end of an answer whose own last paragraph made it obvious.
   * Three statements of one fact, and the only one a reader had to scroll to was the emptiest.
   *
   * Every OTHER line here survives, because each carries something the badge cannot: gaps, a spent
   * budget, a lapsed approval, an org that refused the write, a plan waiting on review. The rule is
   * that this line exists to tell the reader something they could not otherwise know.
   */
  const byOutcome: Partial<Record<Outcome, { text: string; tone: "ok" | "warn" | "bad" }>> = {
    plan_proposed: { text: "Plan ready for your review. Nothing has changed in your org.", tone: "ok" },
    clarification_asked: { text: "Waiting on your answer to continue.", tone: "warn" },
    force_answered: { text: "Answered, though some checks did not finish.", tone: "warn" },
    no_evidence: { text: "Stopped — nothing in the org supported an answer.", tone: "warn" },
    wall_timeout: { text: "Stopped — the run reached its time limit.", tone: "warn" },
    budget_exceeded: { text: "Stopped — the run reached its budget.", tone: "warn" },
    provider_limited: { text: "Stopped — the model provider was rate-limiting.", tone: "warn" },
    plan_approval_expired: { text: "Stopped — the plan approval expired.", tone: "warn" },
    blocked_org: { text: "Stopped — this org is not available for changes.", tone: "bad" },
    cancelled: { text: "Cancelled.", tone: "warn" },
    failed: { text: "The run failed.", tone: "bad" },
  };

  const fromOutcome = run.outcome ? byOutcome[run.outcome] : undefined;
  if (fromOutcome) return fromOutcome;
  // `converged` and an unmapped outcome on a completed run both land here: nothing to add.
  if (run.status === "completed") return null;

  // A terminal run with no outcome the client recognises. Says the status and nothing more — an
  // unmapped enum value must not become a confident sentence about what happened.
  return run.status === "failed"
    ? { text: "The run failed.", tone: "bad" }
    : { text: "Cancelled.", tone: "warn" };
}

// ── Turns ───────────────────────────────────────────────────────────────────

export interface Turn {
  /** The user's message, when the turn has one. A run resumed mid-turn may not. */
  user: Message | null;
  assistant: Message[];
  /**
   * The mode the run was ASKED in (`RunState.run.mode`), for the chip under the user's message.
   *
   * Only meaningful when the reader chose it: `write` and `plan`. `read` is the unmarked default
   * and also what a run created without a hint reports, so it carries no chip. `null` = unknown —
   * an archived turn whose mode the server response does not carry.
   */
  mode?: Mode | null;
  /**
   * This turn started the run that EXECUTES an approved plan (§7i).
   *
   * That run is created with the original request as its `user_intent`, so without this the
   * conversation shows the reader's question a second time, verbatim, as if they had asked twice.
   * What they actually did was press Approve — so the turn says that, with the goal and version
   * they approved. Known only for the run this client started (the backend sets no
   * `parent_run_id`); a reload shows the plain question again.
   */
  approval?: { goal: string; planVersion: number } | null;
  /**
   * Cards for run state that no part carded, kept with the turn that PRODUCED it.
   *
   * Only archived turns carry these: the live run derives its own from `runStateStore` as the state
   * arrives. A past run's changeset card has to come from somewhere once that run is no longer the
   * live one, and this is where — see `ArchivedRun.changeset` and the carried-changeset note in
   * `WeaveTranscript`.
   */
  references?: DerivedReference[];
  /**
   * How the run that produced this turn ENDED, kept with the turn rather than with the screen.
   *
   * REPORTED: a second run failing painted its error at the very top of the conversation, above the
   * first question — "the failure belongs to Run 2, not the entire conversation". Two separate
   * causes, and this field is the second one. `runStateStore` holds exactly ONE run (§3.2: a new
   * run's `state.snapshot` replaces it, and `attach()` resets to `INITIAL` on a new run id), so the
   * moment Run 3 starts, Run 2's errors and terminal status are GONE from the client entirely —
   * a past failure could not be rendered in the right place because it no longer existed anywhere.
   * Recording it per run, on the turn it belongs to, is what lets the transcript show
   * `Run 1 ✓ / Run 2 ✕ / Run 3 ●` at once, each against its own question.
   *
   * Only ARCHIVED turns carry it. The live run's outcome is still read straight from
   * `runStateStore` — it is on screen already and does not need a copy.
   */
  outcome?: ArchivedOutcome | null;
}

/**
 * Pair messages into turns on `turn_id`.
 *
 * `turn_id` is on every message and is the contract's own grouping key, so this needs no heuristics
 * about adjacency. Turn ORDER follows first appearance rather than sorting by id — ids are opaque
 * and sorting them would reorder the conversation.
 *
 * `system` messages are not rendered: none of the three roles is described as user-facing narrative
 * except `user` and `assistant`, and a system message in the transcript is protocol leaking through.
 */
export function toTurns(messages: readonly Message[]): Turn[] {
  const order: string[] = [];
  const byTurn = new Map<string, Turn>();
  for (const message of messages) {
    if (message.role === "system") continue;
    const key = message.turn_id || message.message_id;
    let turn = byTurn.get(key);
    if (!turn) {
      turn = { user: null, assistant: [] };
      byTurn.set(key, turn);
      order.push(key);
    }
    if (message.role === "user") turn.user = turn.user ?? message;
    else turn.assistant.push(message);
  }
  return order.map((key) => byTurn.get(key)!);
}

/**
 * Put the user's own question back into the conversation.
 *
 * THE PROTOCOL DID NOT ALWAYS ECHO IT. `RunState.messages` carried assistant messages only —
 * verified against a live `state.snapshot` from the deployed backend, which held two
 * `role:"assistant"` messages and no `role:"user"` message at all. So the v1 transcript rendered the
 * answer with nothing above it, and a run that had not answered yet rendered as an empty column —
 * the user could not see what they had just asked.
 *
 * THAT GAP IS CLOSED, AND THIS STILL HAS TWO JOBS. The backend now derives a `role:"user"` message
 * from `user_intent` into every `RunState` (`gui_protocol/mappers/message.py::user_message`), which
 * is why a turn restored from `GET /session` needs no `intent` passed alongside it. What remains is
 * the LIVE send, where this client renders the question in the instant between `POST /api/runs`
 * returning and the first frame arriving — and the cleaning below, which applies to the backend's
 * echo as much as to the local copy.
 *
 * THIS IS NOT FABRICATED DATA. `intent` is the text the client itself submitted to `POST /api/runs`
 * moments earlier; the legacy path has always rendered the same string from its own store. Nothing
 * here invents a message id, a `turn_id`, or a part — the synthetic message is marked
 * `message_id: SYNTHETIC_USER_MESSAGE_ID` so a reader of the store can tell it apart from anything
 * the backend sent.
 *
 * It DEFERS to the backend for IDENTITY. If the first turn already carries a user message — a
 * backend that starts projecting one, or a session replay that includes it — that message is kept
 * rather than duplicated with a synthetic one alongside it. Its TEXT still goes through
 * `cleanDisplayIntent` (see `cleanedUserMessage` below): the backend's `user_intent` is not a
 * display field, it is the exact string this client posted, `[Refactor target: …]` header and
 * embedded class source included when one was attached — verified from a real run, where a
 * refactor-target send came back with a `role: "user"` message in `RunState.messages` and this
 * guard, before it also cleaned that message's text, rendered it byte-for-byte in the transcript.
 */
export const SYNTHETIC_USER_MESSAGE_ID = "msg_local_user_intent";

/**
 * Remove exactly one LEADING fenced code block from a string — plain scan, no regex (this file's
 * other string helpers hold to the same convention). Used by `cleanDisplayIntent` below to drop
 * the auto-injected class/context dump that sits immediately after a `[Refactor target: …]` or
 * `[Context from …]` header, WITHOUT touching a fence anywhere else in the string — a fence later
 * on is the reader's OWN code, typed or pasted, and must render untouched.
 */
function stripLeadingCodeFence(value: string): string {
  let i = 0;
  while (i < value.length && (value[i] === " " || value[i] === "\n" || value[i] === "\t" || value[i] === "\r")) i++;
  if (value[i] !== "`" || value[i + 1] !== "`" || value[i + 2] !== "`") return value;
  // Skip the opening fence's own line (the backticks, plus an optional language tag) to its newline.
  let openEnd = i + 3;
  while (openEnd < value.length && value[openEnd] !== "\n") openEnd++;
  if (openEnd >= value.length) return value; // no newline after the opener — malformed, leave it alone
  const close = value.indexOf("```", openEnd + 1);
  if (close < 0) return value; // unterminated — leave the text as-is rather than eat all of it
  let rest = value.slice(close + 3);
  if (rest.startsWith("\n")) rest = rest.slice(1);
  return rest;
}

/**
 * Turn a raw run intent into what the READER should see for it.
 *
 * REPORTED: selecting a class and asking a question showed the file name, its metadata type, an
 * internal UUID, AND the whole class source in the chat — "Weave Chat should display the selected
 * file name and relevant content without exposing the internal file id."
 *
 * THE SHAPE THAT LEAKS. Targeting a class embeds the class as CONTEXT for the backend
 * (`MultiAgentRoute.tsx`'s send handler): `[Refactor target: Name (Type) · id=UUID]` followed by a
 * fenced dump of the class source, then the reader's own typed words. That whole string is what the
 * backend stores as `user_intent` (`multi-step-agentic/backend/app/services/run_history.py`
 * `"intent": sr["user_intent"]` — verbatim, no separate "display" field exists there) — so ANYTHING
 * that reads the intent back from the backend gets the messy version. The route already builds a
 * clean LOCAL display string for the run it just started, but that value lives only in this tab's
 * memory: a follow-up question, a reload, or opening the conversation from history all re-derive
 * the turn from the backend's stored `user_intent`, and the id/metadata-type/code came straight
 * back. This is the single place both paths render a user turn from raw text
 * (`withUserTurn`, called for the live run AND for every archived one — see `sessionTurns`), so it
 * is the one place that needs to know how to clean it, rather than each caller inventing its own
 * version of the same rule (the route's OLD `sentIntent` did exactly that, and only covered the
 * live case).
 *
 * The legacy chat surface already had this exact fix (`multiAgentStore.ts`, `hydrateFromDetail`) —
 * this is that same rule, shared rather than forked, so the two surfaces cannot disagree about what
 * a reader is shown for the same stored intent.
 *
 * `[Context from …]` is a second, historical prefix this also recognises (an MCP context header);
 * no current send path in this codebase produces it, but a run created before this codebase's
 * current shape could still carry one, so the fallback is kept.
 *
 * A string with NEITHER recognised prefix — the ordinary case, and every message the reader types
 * themselves — is returned byte-for-byte unchanged. That is what keeps this safe for the SECOND,
 * unrelated fix it sits next to: a reader's own pasted code, wrapped in a fence by the composer,
 * must reach the screen exactly as they sent it.
 */
/**
 * Force every single newline OUTSIDE a fenced code block into a markdown hard break (two trailing
 * spaces before the `\n` — CommonMark's own syntax for one, not an invented convention), so a
 * reader's own line breaks survive being rendered as markdown instead of plain text.
 *
 * WHY THIS EXISTS. `UserBubble` used to render the reader's own words as literal text
 * (`white-space: pre-wrap`), so every `\n` they typed or pasted was a visible line break, always.
 * Routing that text through the same markdown renderer the assistant's answers use (`TurnView.tsx`
 * — the fix for the code-formatting half of this report) is what lets a fenced paste finally look
 * like code, but plain CommonMark treats a LONE newline inside a paragraph as a SOFT break (a
 * space, not a line break) — so a reader's multi-line message, with no code in it at all, would
 * silently collapse onto fewer visual lines than they typed. That is a real regression of existing
 * behaviour, not a hypothetical: "do not break normal user messages" rules it out.
 *
 * A fenced code block already preserves its own newlines exactly, in any CommonMark implementation
 * — that half needs no help — which is why this only touches the segments OUTSIDE the fences: two
 * trailing spaces added INSIDE a fence would edit the code itself.
 */
export function preserveLineBreaksOutsideFences(text: string): string {
  let out = "";
  let i = 0;
  while (i < text.length) {
    const fenceStart = text.indexOf("```", i);
    if (fenceStart < 0) {
      out += text.slice(i).split("\n").join("  \n");
      break;
    }
    out += text.slice(i, fenceStart).split("\n").join("  \n");
    const fenceEnd = text.indexOf("```", fenceStart + 3);
    if (fenceEnd < 0) {
      out += text.slice(fenceStart); // unterminated fence — leave the rest exactly as given
      break;
    }
    out += text.slice(fenceStart, fenceEnd + 3);
    i = fenceEnd + 3;
  }
  return out;
}

export function cleanDisplayIntent(raw: string | null | undefined): string {
  const s = raw || "";
  if (s.startsWith("[Refactor target:")) {
    const close = s.indexOf("]");
    if (close >= 0) {
      const inner = s.slice("[Refactor target:".length, close).trim();
      let nameEnd = inner.length;
      const paren = inner.indexOf(" (");
      const dot = inner.indexOf(" \u00b7"); // " ·"
      if (paren >= 0) nameEnd = Math.min(nameEnd, paren);
      if (dot >= 0) nameEnd = Math.min(nameEnd, dot);
      const name = inner.slice(0, nameEnd).trim();
      const prefix = name ? `[File: ${name}] ` : "";
      const rest = stripLeadingCodeFence(s.slice(close + 1));
      return (prefix + rest.trim()).trim() || raw || "";
    }
  } else if (s.startsWith("[Context from ")) {
    const close = s.indexOf("]");
    if (close >= 0) {
      const prefix = `${s.slice(0, close + 1)} `;
      const rest = stripLeadingCodeFence(s.slice(close + 1));
      return (prefix + rest.trim()).trim() || raw || "";
    }
  }
  return s;
}


/**
 * A backend-provided user message, with its TEXT part(s) run through `cleanDisplayIntent`.
 *
 * The synthetic fallback below is not the only place a `[Refactor target: …]` header and an
 * embedded class dump can reach the screen. A backend that echoes the user's own message back — a
 * real `role: "user"` message in `RunState.messages`, or a session-restore response that includes
 * one — sends back the EXACT string this client posted, because `user_intent` is the wire value,
 * never a separate display copy (`run_history.py`: `"intent": sr["user_intent"]`, verbatim).
 * Deferring to that message's IDENTITY — same `message_id`, same part order, same position in the
 * turn — is still correct; only the TEXT a reader sees needs the same cleaning the synthetic path
 * already gets, so this applies the identical rule rather than a second one.
 *
 * Only `type: "text"` parts are touched, and each independently — an attachment reference or a
 * future `citation` part rides through unchanged, and a message with more than one text part (not
 * seen from a user today, but nothing rules it out) gets every one cleaned rather than only the
 * first. `cleanDisplayIntent` is a no-op on a string with neither recognised prefix (its own
 * contract), so an ordinary typed message — the overwhelming majority — returns the SAME message
 * object, not a new one with identical content.
 */
function cleanedUserMessage(message: Message): Message {
  let changed = false;
  const parts = message.parts.map((part) => {
    if (part.type !== "text") return part;
    const raw = partText(part);
    const cleaned = cleanDisplayIntent(raw);
    if (cleaned === raw) return part;
    changed = true;
    return { ...part, body: { ...(part.body as Record<string, unknown> | null), text: cleaned } };
  });
  return changed ? { ...message, parts } : message;
}

export function withUserTurn(turns: Turn[], intent: string | null | undefined): Turn[] {
  // The FIRST turn is this run's: `RunState` is per-run, so there is exactly one exchange in it.
  // A message the backend already provided keeps its identity; only its text is cleaned.
  if (turns[0]?.user) {
    const cleaned = cleanedUserMessage(turns[0].user);
    return cleaned === turns[0].user ? turns : [{ ...turns[0], user: cleaned }, ...turns.slice(1)];
  }
  // `cleanDisplayIntent` FIRST, `.trim()` second: it already trims its own output, but a raw
  // intent with no recognised prefix passes through byte-for-byte, and this keeps the emptiness
  // check below correct for that case the same way it always was.
  const text = typeof intent === "string" ? cleanDisplayIntent(intent).trim() : "";
  if (!text) return turns;

  const user = {
    message_id: SYNTHETIC_USER_MESSAGE_ID,
    role: "user",
    turn_id: turns[0]?.assistant[0]?.turn_id ?? "t1",
    status: "complete",
    parts: [
      {
        part_id: `${SYNTHETIC_USER_MESSAGE_ID}_0`,
        message_id: SYNTHETIC_USER_MESSAGE_ID,
        index: 0,
        type: "text",
        state: "available",
        body: { text },
      },
    ],
  } as unknown as Message;

  // No turns yet is the case that mattered most: the question must be on screen from the moment it
  // is sent, not once the first assistant frame arrives.
  if (!turns.length) return [{ user, assistant: [] }];
  return [{ ...turns[0], user }, ...turns.slice(1)];
}

// ── Sources ─────────────────────────────────────────────────────────────────

export interface SourceChip {
  citationId: string;
  /** The producer's own words for what this is. Never an id. */
  label: string;
  /** `Citation.source` — what kind of thing it points at. */
  source: string;
  /** Set when the citation names a component, which the changeset board can open. */
  componentId: string | null;
  /** `false` renders an UNVERIFIED marker on the chip — the contract's §2.20 rule. */
  verified: boolean;
  unverifiedReason: string | null;
}

/**
 * The citations an assistant message carries, as chips.
 *
 * WHY THIS EXISTS. `groupParts` drops `citation` parts on the floor with the note that they "ride
 * the prose they annotate" — and then nothing rode anything: the surface received real citations,
 * with `verified` flags and component ids, and showed none of them. Legacy renders a sources strip
 * and clickable chips, so this was a straight regression. Verified against a live `state.snapshot`
 * from production, which carried two citation parts (`emit_code`, `metadata.read_source_code`) plus
 * their ids on the text part's `citations` array.
 *
 * NOT the per-claim anchor treatment yet. `Part.anchor{start,end}` is on the wire and would let an
 * UNVERIFIED marker sit inline on the exact character range; that needs the markdown renderer to
 * accept ranges and is deliberately out of this pass. A chip row under the answer is the honest
 * intermediate: everything shown is a real citation, and nothing claims a precision it does not have.
 */
export function sourcesOf(parts: readonly Part[]): SourceChip[] {
  const out: SourceChip[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    if (part.type !== "citation") continue;
    const body = (part.body ?? {}) as {
      citation_id?: unknown;
      label?: unknown;
      source?: unknown;
      component_id?: unknown;
      verified?: unknown;
      unverified_reason?: unknown;
    };
    const citationId =
      typeof body.citation_id === "string" && body.citation_id ? body.citation_id : part.part_id;
    if (seen.has(citationId)) continue;
    seen.add(citationId);
    const label = typeof body.label === "string" && body.label.trim() ? body.label.trim() : null;
    // No label, no chip: a chip wearing a `cit_…` id tells the reader nothing.
    if (!label) continue;
    out.push({
      citationId,
      label,
      source: typeof body.source === "string" ? body.source : "doc",
      componentId: typeof body.component_id === "string" ? body.component_id : null,
      // Absent `verified` is NOT "verified". §2.20 makes it required; a producer that omits it has
      // not told us the claim was checked.
      verified: body.verified === true,
      unverifiedReason:
        typeof body.unverified_reason === "string" ? body.unverified_reason : null,
    });
  }
  return out;
}

// ── The turn's PHASE — the presentation layer's own state ───────────────────

/**
 * What the reader is living through, right now, in this turn.
 *
 * WHY THIS EXISTS. The transcript used to map parts → blocks and stop there, which is protocol
 * structure rendered directly. It had no notion of "the moment after Send", "the live edge" or "the
 * end of an answer" — so there was nowhere to put a skeleton, a cursor or a completion block, and
 * the surface showed an empty column for the ten seconds before the first part arrived.
 *
 *   idle       nothing has been asked. No skeleton, no line — the empty state owns the screen.
 *   awaiting   a question is in, the assistant has produced NOTHING. Reserve the answer's shape.
 *   working    parts exist but no visible prose yet — reasoning, a tool call. Say what it is doing.
 *   streaming  prose is on screen and the run is still going. Show the live edge.
 *   waiting    the run is parked on a question. THE READER is the one being waited on.
 *   complete   the run is terminal. Close the turn and offer the next step.
 *
 * `waiting` earns its place by being the state a spinner gets WRONG. A run parked on a clarify has
 * no assistant parts, so it read as `awaiting` and rendered the warm-up skeleton — Weave looking
 * busy while it was in fact waiting for the reader, which is the single most confusing thing this
 * surface could say. The interaction card is the whole message in that state; the transcript adds
 * nothing.
 *
 * Derived, never stored: every input is already in `RunState`, and a second copy would be a second
 * thing to keep true.
 */
export type TurnPhase = "idle" | "awaiting" | "working" | "streaming" | "waiting" | "complete";

/**
 * The contract's terminal `RunStatus` values.
 *
 * Exported because `useProtocolRunStream` closes the SSE connection on them, and a second copy of
 * this list is how a run ends up terminal for the transcript and still live for the stream —
 * exactly the disagreement that left a finished run reconnecting once a minute.
 */
export const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set([
  "completed",
  "failed",
  "cancelled",
  "expired",
]);

export function turnPhase(
  runStatus: string | null | undefined,
  turn: Turn | undefined,
): TurnPhase {
  if (runStatus && TERMINAL_RUN_STATUSES.has(runStatus)) return "complete";
  if (runStatus === "input_required") return "waiting";
  /*
   * NOTHING ASKED YET → NOTHING TO NARRATE.
   *
   * Caught by audit, not by looking: without this, an idle `/weave/v1` with no run had
   * `runStatus === null` and no turns, fell through to the parts check, and returned `awaiting` —
   * so the warm-up skeleton rendered on a page where nobody had asked anything, directly above the
   * empty state's "Ask Weave to change something". A regression introduced by this pass.
   *
   * A turn is the right test rather than a run id: `withUserTurn` puts the reader's own question in
   * the transcript the moment they press Send, which is before the first snapshot arrives — so this
   * distinguishes "idle page" from "run starting" without another store read.
   */
  if (!turn) return "idle";
  const parts = turn.assistant.flatMap((message) => message.parts ?? []);
  if (!parts.length) return "awaiting";
  /*
   * VISIBLE PROSE is the boundary, not "any part". A run that has emitted two reasoning parts and a
   * tool call has plenty of parts and nothing a reader can read: calling that `streaming` would put
   * a cursor under a collapsed disclosure and call it an answer.
   */
  const hasProse = parts.some((part) => part.type === "text" && partText(part).trim().length > 0);
  return hasProse ? "streaming" : "working";
}

/**
 * The activity at the LIVE EDGE of a turn — the newest part still `streaming`, and what it is.
 *
 * Read off part state only (§1.4.7): `message.part_start` opens a part `streaming`,
 * `message.part_end` closes it; `tool.call_start` opens a `tool_call` part, `tool.call_end` closes
 * it. The newest open part, by message order then `index`, is where the stream currently is. No
 * timer, no guess: `null` means nothing is open — the run may still be live (the projector emits
 * nothing between hops), which the caller reads from `run.status`, not from here.
 *
 *   reasoning  the model is thinking — "Thinking…"
 *   tool       a tool is running — its own `display_name`, never invented
 *   text       the answer is being written — "Writing…"
 */
export function liveEdgeActivity(
  turn: Turn | undefined,
): { kind: "reasoning" | "tool" | "text"; label: string } | null {
  if (!turn) return null;
  const open = turn.assistant
    .flatMap((message) => [...(message.parts ?? [])].sort((a, b) => a.index - b.index))
    .filter((part) => part.state === "streaming" && part.type !== "citation")
    .at(-1);
  if (!open) return null;
  if (open.type === "reasoning") return { kind: "reasoning", label: "Thinking…" };
  if (open.type === "tool_call") {
    const body = (open.body ?? {}) as { display_name?: unknown; name?: unknown };
    const name =
      (typeof body.display_name === "string" && body.display_name.trim()) ||
      (typeof body.name === "string" && body.name.trim()) ||
      "";
    return { kind: "tool", label: name ? `Working — ${name}` : "Working…" };
  }
  return { kind: "text", label: "Writing…" };
}

/**
 * What to SAY during `working`, in the backend's own words.
 *
 * `tool.call_start` carries a `display_name` that exists to be shown ("Read Opportunity metadata"),
 * so a working line can name the real activity instead of inventing a stage. Returns null when
 * nothing is in flight — the caller then says only that it is working, which is the honest floor.
 *
 * NEVER reasoning text. That is chain-of-thought; it belongs behind the collapsed disclosure the
 * turn already renders, not in a status line.
 */
export function workingActivity(turn: Turn | undefined): string | null {
  const parts = (turn?.assistant ?? []).flatMap((message) => message.parts ?? []);
  let latest: string | null = null;
  for (const part of parts) {
    if (part.type !== "tool_call") continue;
    const line = toolLine(part);
    // Last running call wins: the tail of the list is the most recent thing started.
    if (line.state === "running") latest = line.label;
  }
  return latest;
}

// ── Live subtitles for reference cards ──────────────────────────────────────

/**
 * The one-line subtitle under a reference card's title, from LIVE state.
 *
 * WHY LIVE AND NOT FROM THE PART. The part carries a `ref` and a display `meta`; the current shape
 * of the thing it refers to is in `RunState`. A card that said "3 steps" from a part emitted ten
 * frames ago would contradict the plan pane it opens after a replan.
 *
 * Returns `null` rather than a zero when there is nothing true to say — an "0 components" subtitle
 * on a changeset that has not been built yet reads as an empty changeset.
 */
export type SubtitleSlices = Pick<RunState, "plan" | "changeset" | "deploy" | "evidence">;

export function subtitleFor(target: ReferenceTarget, state: Partial<SubtitleSlices> | null): string | null {
  if (!state) return null;
  switch (target) {
    case "plan": {
      const plan = state.plan;
      if (!plan) return null;
      const steps = plan.steps?.length ?? 0;
      const parts: string[] = [];
      if (steps > 0) parts.push(`${steps} step${steps === 1 ? "" : "s"}`);
      // `change_class` is a closed enum (config|code|mixed|undecided|null). `undecided` is not shown:
      // it is the backend saying it does not know yet, which is not worth a chip.
      if (plan.change_class && plan.change_class !== "undecided") parts.push(plan.change_class);
      return parts.length ? parts.join(" · ") : null;
    }
    case "changeset": {
      const changeset = state.changeset;
      const components = changeset?.components ?? [];
      if (!changeset || !components.length) return null;
      /*
       * BROKEN DOWN BY `Component.op`, not just counted.
       *
       * "4 components" tells a reader the size of the change; "4 components · 2 new · 1 deleted"
       * tells them its RISK, which is what decides whether they open the diff before approving. The
       * ops come from `Component.op` — a closed enum — and only the non-zero ones are named, so a
       * pure-create changeset does not carry two zeroes.
       */
      const by = { create: 0, update: 0, delete: 0 } as Record<string, number>;
      for (const component of components) {
        const op = typeof component.op === "string" ? component.op : "";
        if (op in by) by[op] += 1;
      }
      const n = components.length;
      const detail = [
        by.create ? `${by.create} new` : null,
        by.update ? `${by.update} changed` : null,
        by.delete ? `${by.delete} deleted` : null,
      ].filter(Boolean);
      const base = `${n} component${n === 1 ? "" : "s"}`;
      return detail.length ? `${base} · ${detail.join(" · ")}` : base;
    }
    case "deploy": {
      const deploy = state.deploy;
      if (!deploy) return null;
      const failed = (deploy.phases ?? []).reduce((n, phase) => n + (phase.counts?.failed ?? 0), 0);
      switch (deploy.state) {
        case "running":
          return "in progress";
        case "succeeded":
          return "completed";
        case "failed":
          return failed > 0 ? `${failed} component${failed === 1 ? "" : "s"} failed` : "failed";
        // Never "succeeded" and never "failed" — §7d's amber state, said in words.
        case "deployed_grants_failed":
          return "deployed, access not granted";
        case "reverted":
          return "reverted";
        default:
          return null;
      }
    }
    case "evidence": {
      const items = Object.values(state.evidence ?? {});
      if (!items.length) return null;
      // The same predicate the rail, the button and the server's 409 use.
      const unmet = items.filter((e) => e.blocking_for_approval && e.status !== "ready").length;
      return unmet > 0
        ? `${unmet} required check${unmet === 1 ? "" : "s"} outstanding`
        : "all required checks finished";
    }
    case "artifact":
      // The part's own `meta.title` is the whole label; a subtitle would repeat it.
      return null;
    default:
      return null;
  }
}

// ── Derived references ──────────────────────────────────────────────────────

export interface DerivedReference {
  target: ReferenceTarget;
  title: string;
  subtitle: string | null;
  cta: string;
  /** Amber/red states get emphasis; everything else is quiet. */
  tone: "quiet" | "warn" | "bad";
  /**
   * The produced code, previewed under a `changeset` card — see `changePreviews`. Absent on every
   * other target, and on a changeset whose files carry no body yet.
   */
  previews?: ChangePreview[];
  /** How many more changed files the previews did not show. */
  previewsHidden?: number;
}

/**
 * Reference cards for state the transcript has NOT already been given a part for.
 *
 * WHY THIS EXISTS, and it is the most important guard in this file. The reference parts
 * (`plan`/`changeset`/`deploy`/`evidence`) are the contract's intended mechanism, and the transcript
 * renders them when they arrive. But NOTHING IN THE CONTRACT PROMISES A PRODUCER EMITS THEM —
 * `Part.type` defines the vocabulary, not an obligation, and this codebase already has one scar from
 * assuming otherwise: `RunState.artifacts[]` is permanently empty because no artifact type has a
 * mapper registrant (handoff gotcha 2), so a viewer built on listing it showed nothing and looked
 * broken.
 *
 * A conversation that only reflected emitted parts would therefore have a FAILED DEPLOY be
 * invisible — the run state says `deploy.state === "failed"` while the transcript says nothing but
 * "The run failed." That was the state of the very first screenshot of this design.
 *
 * So: parts first, derivation second, and never both for the same subject. `seen` is the set of
 * targets already carded by a part, and anything in it is skipped here.
 *
 * DERIVED CARDS ARE NOT INVENTED CONTENT. Each one reports a field the contract defines and the run
 * state holds — the plan's step count, the changeset's component count, the deploy's state, the
 * count of unmet blocking evidence. Nothing here fabricates a subject that does not exist: a run
 * with no plan gets no plan card.
 */
/**
 * Are the deploy and evidence cards derived into the conversation?
 *
 * OFF, by request, until the backend's deploy and evidence state is something a card can stand on.
 * What was on screen: "Checks outstanding · 1 required check outstanding" and "Deployment failed ·
 * failed" stacked above a changeset approval, on a run whose deploy the backend does not yet fully
 * drive end to end — so the cards reported a state the reader could neither act on nor trust. The
 * derivation is kept (tests pass `{ runStatusCards: true }` to exercise it) and the workspace's
 * deploy and evidence sections are untouched; only the conversation stops leading with them. Flip
 * this once the gap recorded in `docs/WEAVE_BACKEND_GAPS.md` closes.
 */
export const DERIVED_RUN_STATUS_CARDS = false;

export function deriveReferences(
  state: Partial<SubtitleSlices> | null,
  seen: ReadonlySet<ReferenceTarget>,
  { runStatusCards = DERIVED_RUN_STATUS_CARDS }: { runStatusCards?: boolean } = {},
): DerivedReference[] {
  if (!state) return [];
  const out: DerivedReference[] = [];

  const add = (target: ReferenceTarget, title: string, tone: DerivedReference["tone"]) => {
    if (seen.has(target)) return;
    const card: DerivedReference = { target, title, subtitle: subtitleFor(target, state), cta: CTA[target], tone };
    if (target === "changeset") {
      // The code itself, under the card — the bodies are already in `Component.files[]`.
      const { previews, hidden } = changePreviews(state.changeset);
      if (previews.length) {
        card.previews = previews;
        card.previewsHidden = hidden;
      }
    }
    out.push(card);
  };

  /*
   * ORDER IS THE STORY'S: what was proposed, what will change, what is required before it can, what
   * happened. It deliberately does not follow `RunState`'s field order.
   */
  /*
   * NO derived plan card. The plan is summarised in the conversation by `PlanSummaryBlock` —
   * headline, step titles, the §3.1 promise and its own "Review plan" — so a card saying
   * "Plan · 3 steps" beside it would be the same subject offered twice, with the card the less
   * useful of the two. `planSummary` is the plan's presence in the chat.
   */
  if (state.changeset?.components?.length) add("changeset", "Changes", "quiet");
  if (!runStatusCards) return out;

  // Evidence is carded ONLY when something is outstanding. A run whose checks all passed does not
  // need a row saying so in the conversation — that is inspection detail, and the workspace has it.
  const evidence = Object.values(state.evidence ?? {});
  const unmet = evidence.filter((e) => e.blocking_for_approval && e.status !== "ready");
  if (unmet.length) add("evidence", "Checks outstanding", "warn");

  const deploy = state.deploy;
  if (deploy) {
    // §7d: `deployed_grants_failed` is neither success nor failure, and it is the one state most
    // likely to be read as either. It gets its own title, not a shared one.
    const tone: DerivedReference["tone"] =
      deploy.state === "failed" ? "bad" : deploy.state === "deployed_grants_failed" ? "warn" : "quiet";
    const title =
      deploy.state === "failed"
        ? "Deployment failed"
        : deploy.state === "deployed_grants_failed"
          ? "Deployed, access not granted"
          : deploy.state === "succeeded"
            ? "Deployment complete"
            : deploy.state === "reverted"
              ? "Reverted"
              : "Deployment";
    add("deploy", title, tone);
  }

  return out;
}

const CTA: Record<ReferenceTarget, string> = {
  plan: "Review plan",
  changeset: "Review changes",
  deploy: "View details",
  evidence: "View checks",
  artifact: "Open",
};

/** The targets a turn's parts have already carded — the input to `deriveReferences`. */
export function cardedTargets(messages: readonly Message[]): Set<ReferenceTarget> {
  const seen = new Set<ReferenceTarget>();
  for (const message of messages) {
    for (const part of message.parts ?? []) {
      const card = referenceCard(part);
      if (card) seen.add(card.target);
    }
  }
  return seen;
}

/**
 * Should the closing line render, given the cards above it?
 *
 * "The run failed." directly under a card that already says "Deployment failed · 2 components
 * failed · View details" is the same news twice, and the card is the more specific and the more
 * actionable of the two. Suppressing the line is not hiding the failure — it is not saying it a
 * second time, less usefully.
 *
 * Only the BAD case is suppressed. A warning line ("Finished, but not everything was completed")
 * says something no card does: that the RUN fell short, which is a different subject from any one
 * deploy or check. And an `ok` line is the only thing marking the end of a successful turn.
 */
export function showClosingLine(
  closing: { tone: "ok" | "warn" | "bad" } | null,
  derived: readonly DerivedReference[],
): boolean {
  if (!closing) return false;
  if (closing.tone !== "bad") return true;
  return !derived.some((reference) => reference.tone === "bad");
}

// ── The plan summary in the conversation ────────────────────────────────────

/** How many step titles the chat shows before the rest go behind a count. */
export const PLAN_SUMMARY_STEPS = 5;
/** While the plan RUNS, the window of steps the conversation shows around the live one. */
export const PLAN_PROGRESS_STEPS = 6;

/** One component a step touches — `ComponentRef`, as the chip under the step reads it. */
export interface PlanSummaryComponent {
  /** `full_name` — `Opportunity.Approval_Region__c`, `OpportunityApprovalService`. */
  name: string;
  /** `metadata_type` — `CustomField`, `ApexClass`. */
  type: string;
  op: string | null;
}

/**
 * One step, as the conversation reads it: the title, its status, WHAT it touches and WHY.
 *
 * WHY MORE THAN THE TITLE. "Approval routing service" says nothing about what happens to the org;
 * "Approval routing service · OpportunityApprovalService (ApexClass, create)" says a class is being
 * written. The components are the plan's concrete claim about the org, and a reader judging a
 * proposal — or watching it run — should not have to open the plan pane to learn it. `rationale`
 * is the backend's own "why this mechanism" (§2.8), shown only at the step that is live or broke.
 */
export interface PlanSummaryRow {
  title: string;
  status: StepStatus;
  components: PlanSummaryComponent[];
  rationale: string | null;
}

export interface PlanSummary {
  /** "I can make this change in 3 steps." — or the past tense once it is running. */
  headline: string;
  /** `PlanStep.title` only, capped. Nothing else from the step. */
  steps: string[];
  /**
   * EVERY step, in plan order, with the status the block reads (after the live-step inference
   * below). `steps`/`statuses` are the WINDOW the block shows folded; this is what "Show all N
   * steps" reveals, and where the component chips come from.
   */
  rows: PlanSummaryRow[];
  /** `Plan.doc_artifact_id` — the plan as a document. `null` until the backend has rendered one. */
  docRef: string | null;
  /** One per shown step, aligned with `steps`. All `pending` for a proposal. */
  statuses: StepStatus[];
  /** How many titles AFTER the shown ones were not shown. 0 when all of them are. */
  hidden: number;
  /**
   * How many EARLIER steps the window moved past — every one of them finished. Only non-zero while
   * the plan runs: a proposal is read from the top, a running plan is read at the live edge.
   */
  hiddenBefore: number;
  /** §3.1's promise, and only while it is still true. */
  promise: string | null;
  /** How far a RUNNING plan has got. `null` for a proposal. */
  progress: { done: number; total: number; failed: number } | null;
  /**
   * What the plan is, right now, as ONE word for the status pill.
   *
   *   proposal   nothing has run (draft / awaiting approval)
   *   running    the execute run is live
   *   waiting    the run is parked on a question
   *   finished   the run ended and every step is done (or skipped)
   *   partial    the run ended `completed` but some steps were never reported done
   *   failed     the run ended `failed`, or a step failed
   *   stopped    the run was cancelled or expired
   *
   * `finished`/`partial`/`failed`/`stopped` come from the RUN's terminal status, because the backend
   * never touches step statuses when a run ends — a step left `running` by a cancelled run stays
   * `running` on the wire, and only the run's own status says what happened to it.
   */
  outcome: "proposal" | "running" | "waiting" | "finished" | "partial" | "failed" | "stopped";
  /**
   * True when the `running` status in `statuses` was INFERRED here rather than reported.
   *
   * Step status on a write run is model-authored (`update_plan`: "call it when you start a step,
   * when you finish one"), and a model that never calls it leaves every step `pending` while the
   * run visibly works. The plan is an ordered checklist, so while the run is live the first
   * unfinished step after the last finished one is the one being worked — shown with the same
   * spinner, and said as "assumed" to assistive tech so the inference is never passed off as a
   * report.
   */
  currentInferred: boolean;
}

/**
 * What the conversation says about a plan.
 *
 * TITLES ONLY, AND THIS IS THE POINT. A `PlanStep` also carries `lane`, `phase`, `op`, `mechanism`,
 * `layer`, `components`, `depends_on`, `evidence_ids`, `task_ids` and a `step_id` — and the chat
 * shows none of them. `title` is one short line by contract (max 400), which is exactly the
 * granularity a reader needs to judge whether the shape of the work is right. Everything else is
 * what the workspace is for.
 *
 * The card that used to stand here said "2 steps · mixed", which told the reader the plan's SIZE
 * and made them open a panel to learn its CONTENT.
 *
 * THE PROMISE IS CONDITIONAL. §3.1: a plan-mode run terminates having emitted nothing, so "nothing
 * has changed in your org yet" is true for a proposal and a lie once `status` is `executing` or
 * `done`. It is read off `Plan.status`, never assumed.
 */
export function planSummary(
  plan: Plan | null,
  options: {
    /** The run is still executing — the only state in which a current step may be inferred. */
    live?: boolean;
    /** `RunState.run.status`, for the terminal reading of steps the backend never closed. */
    runStatus?: RunStatus | null;
  } = {},
): PlanSummary | null {
  if (!plan) return null;
  const rows: PlanSummaryRow[] = (plan.steps ?? [])
    .map((step) => ({
      title: typeof step.title === "string" ? step.title.trim() : "",
      status: (typeof step.status === "string" ? step.status : "pending") as StepStatus,
      // Only what `ComponentRef` carries: identity, type and op. No path and no body — a plan step
      // names a component, it does not hold its code; that lives in the changeset once written.
      components: (step.components ?? [])
        .filter((c) => c && typeof c.full_name === "string" && c.full_name.trim())
        // NO `component_id`: a chip is read, never resolved, and an id in the conversation's model
        // is one render away from being on screen. The pane and the board have the ids.
        .map((c) => ({
          name: c.full_name.trim(),
          type: typeof c.metadata_type === "string" ? c.metadata_type : "",
          op: typeof c.op === "string" && c.op ? c.op : null,
        })),
      rationale: typeof step.rationale === "string" && step.rationale.trim() ? step.rationale.trim() : null,
    }))
    .filter((row) => row.title.length > 0);
  const docRef = typeof plan.doc_artifact_id === "string" && plan.doc_artifact_id ? plan.doc_artifact_id : null;

  // A plan with no readable step is not summarisable — and a headline promising "3 steps" over an
  // empty list would be worse than the card it replaced.
  if (!rows.length) return null;

  const proposal = plan.status === "draft" || plan.status === "awaiting_approval";
  const n = rows.length;
  const plural = n === 1 ? "" : "s";

  if (proposal) {
    const shown = rows.slice(0, PLAN_SUMMARY_STEPS);
    return {
      headline: `I can make this change in ${n} step${plural}.`,
      steps: shown.map((row) => row.title),
      statuses: shown.map(() => "pending" as StepStatus),
      rows,
      docRef,
      hidden: Math.max(0, n - PLAN_SUMMARY_STEPS),
      hiddenBefore: 0,
      promise: "Nothing has changed in your org yet.",
      progress: null,
      currentInferred: false,
      outcome: "proposal",
    };
  }

  /*
   * ── A RUNNING PLAN IS READ AT ITS LIVE EDGE ─────────────────────────────────────────────────
   *
   * The statuses come straight off `Plan.steps[].status` — `plan.updated` versions and
   * `plan.step_status` frames, both reduced into the store; nothing is inferred here. The window
   * keeps the step being worked on in view with one finished step above it for context, so a
   * twelve-step plan on step ten does not show steps one to six and hide the one that matters.
   */
  const done = rows.filter((row) => row.status === "done").length;
  const failed = rows.filter((row) => row.status === "failed").length;
  const settled = (status: StepStatus) => status === "done" || status === "skipped" || status === "deferred";
  /*
   * THE CURRENT STEP, WHEN NOBODY SAID WHICH ONE IT IS. Only while the run is live, only when no
   * step carries a live status of its own, and only the first `pending` step AFTER the last step
   * that was marked at all — so a model that ticked step 3 without ticking 1 and 2 is not told it
   * is "on step 1". Anything the backend reports wins over this.
   */
  let currentInferred = false;
  if (options.live && !rows.some((row) => row.status === "running" || row.status === "waiting_input")) {
    let lastMarked = -1;
    rows.forEach((row, index) => {
      if (row.status !== "pending") lastMarked = index;
    });
    const candidate = rows.findIndex((row, index) => index > lastMarked && row.status === "pending");
    if (candidate >= 0) {
      rows[candidate] = { ...rows[candidate], status: "running" };
      currentInferred = true;
    }
  }
  const firstActive = rows.findIndex((row) => !settled(row.status));
  const anchor = firstActive < 0 ? n - 1 : firstActive;
  const start = Math.max(0, Math.min(anchor - 1, n - PLAN_PROGRESS_STEPS));
  const shown = rows.slice(start, start + PLAN_PROGRESS_STEPS);

  /*
   * ── THE OUTCOME IS THE RUN'S, THEN THE STEPS' ───────────────────────────────────────────────
   *
   * A cancelled run leaves its steps exactly as they were; a failed run does not mark the step it
   * died in; a completed run whose model never called `update_plan` leaves every step pending. So
   * the run's terminal status decides the word, and the step statuses only refine it.
   */
  const runStatus = options.runStatus ?? null;
  const allSettled = firstActive < 0;
  let outcome: PlanSummary["outcome"];
  if (runStatus === "cancelled" || runStatus === "expired") outcome = "stopped";
  else if (runStatus === "failed" || failed > 0) outcome = "failed";
  else if (runStatus === "completed" || plan.status === "done") outcome = allSettled ? "finished" : "partial";
  else if (runStatus === "input_required") outcome = "waiting";
  else if (allSettled && failed === 0) outcome = "finished";
  else outcome = "running";

  const headline =
    outcome === "finished"
      ? `Finished ${n} step${plural}.`
      : outcome === "partial"
        ? `Finished — ${done} of ${n} step${plural} reported done.`
        : outcome === "failed"
          ? `Stopped after ${done} of ${n} step${plural}${failed ? ` — ${failed} failed` : ""}.`
          : outcome === "stopped"
            ? `Stopped after ${done} of ${n} step${plural}.`
            : outcome === "waiting"
              ? `Waiting on you — ${done} of ${n} step${plural} done.`
              : `Working through ${n} step${plural}${done ? ` — ${done} done` : ""}.`;

  return {
    headline,
    steps: shown.map((row) => row.title),
    statuses: shown.map((row) => row.status),
    rows,
    docRef,
    hidden: Math.max(0, n - start - shown.length),
    hiddenBefore: start,
    promise: null,
    progress: { done, total: n, failed },
    currentInferred,
    outcome,
  };
}

// ── Outputs — the things Weave produced ─────────────────────────────────────

export interface RunOutput {
  /** `Part.ref` — the artifact id. Never rendered; used to open it. */
  ref: string;
  /** `meta.title` when the producer sent one, else a human default. Never the id. */
  title: string;
  /** Where opening it goes. */
  target: Extract<ReferenceTarget, "artifact" | "changeset">;
}

/**
 * The run's generated work, assembled from what the contract actually carries.
 *
 * NOT FROM `RunState.artifacts[]`, WHICH IS ALWAYS EMPTY. No artifact type has a
 * `gui_protocol/mappers` registrant (handoff gotcha 2), so a list built by enumerating it would
 * render nothing and look broken — the same scar that made `deriveReferences` necessary. The
 * sources here are the ones that do carry data:
 *
 *   · `Part.type: "artifact"` refs seen in the transcript — the producer naming a document;
 *   · `Plan.doc_artifact_id` — the rendered plan document, which exists on every planned run;
 *   · `Part.type: "diff"` refs, as the changeset they belong to.
 *
 * Deduplicated on `ref`, because a document mentioned in two turns is one output. Ordered by first
 * appearance, then the plan doc last: the plan is a by-product of the request, and the things the
 * assistant generated in answer to it are what the reader came for.
 *
 * Empty is the common case and returns `[]` — the caller renders nothing rather than an empty
 * shelf.
 */
export function runOutputs(
  messages: readonly Message[],
  plan: Plan | null,
  {
    /**
     * Whether `plan.doc_artifact_id` gets a row here.
     *
     * `false` when the plan block is on screen: it carries its own "plan.md" link, so a Documents
     * row for the same artifact would be the plan offered a third time — block, document row, pane
     * — which is exactly the "three representations of one thing" a reader cannot tell apart. The
     * artifact is unchanged and still opens through the same Document section; only the doorway
     * moves into the block that describes it.
     */
    planDoc = true,
  }: { planDoc?: boolean } = {},
): RunOutput[] {
  const seen = new Set<string>();
  const out: RunOutput[] = [];

  for (const message of messages) {
    for (const part of [...(message.parts ?? [])].sort((a, b) => a.index - b.index)) {
      if (part.type !== "artifact") continue;
      const ref = part.ref;
      if (typeof ref !== "string" || !ref || seen.has(ref)) continue;
      seen.add(ref);
      const meta = (part.meta ?? {}) as { title?: unknown };
      const titled = typeof meta.title === "string" && meta.title.trim() ? meta.title.trim() : null;
      out.push({ ref, title: titled ?? "Document", target: "artifact" });
    }
  }

  const doc = plan?.doc_artifact_id;
  if (planDoc && typeof doc === "string" && doc && !seen.has(doc)) {
    seen.add(doc);
    out.push({ ref: doc, title: "plan.md", target: "artifact" });
  }

  return out;
}

/**
 * The SESSION's turns: every run before this one, then this one live.
 *
 * WHY THIS IS A FUNCTION AND NOT A `useMemo` BODY. It is the whole of the multiple-questions fix,
 * and the failure it corrects — a second question erasing the first — is invisible in a single-run
 * render, which is all a component test or the QA harness can produce. Pure, it can be given two
 * archived runs and a live one and asked what the reader sees.
 *
 * THE LIVE RUN IS NEVER TAKEN FROM THE ARCHIVE. `archived` is written continuously as frames arrive,
 * so it contains an entry for the run that is currently streaming too; rendering that entry as well
 * would show the same exchange twice, the older copy one frame behind. `runId` is what excludes it,
 * and `messages` — the authoritative streaming copy from `runStateStore` — is what replaces it.
 *
 * Order is archive order, which is the order the runs happened in, then the live run last. A
 * conversation reads downward (§8.1) and a new answer belongs at the bottom.
 */
export function sessionTurns(
  archived: readonly {
    runId: string;
    userIntent: string | null;
    messages: readonly Message[];
    mode?: Mode | null;
    changeset?: ArchivedChangeset | null;
    outcome?: ArchivedOutcome | null;
  }[],
  runId: string | null,
  messages: readonly Message[],
  userIntent: string | null | undefined,
  /** The live run's `run.mode`; stamped on ITS turn only. */
  liveMode: Mode | null = null,
  /** The live run executes an approved plan — its first turn is the approval, not a re-ask. */
  liveApproval: { goal: string; planVersion: number } | null = null,
): Turn[] {
  const past = archived
    .filter((entry) => !runId || entry.runId !== runId)
    .flatMap((entry) =>
      withOutcome(
        withReferences(
          withMode(withUserTurn(toTurns(entry.messages), entry.userIntent), entry.mode ?? null),
          entry.changeset ?? null,
        ),
        entry.outcome ?? null,
      ),
    );
  const live = withMode(withUserTurn(toTurns(messages), userIntent), liveMode).map((turn, index) =>
    index === 0 && liveApproval ? { ...turn, approval: liveApproval } : turn,
  );
  return [...past, ...live];
}

/**
 * How a run ended, kept for a turn that is no longer the live one.
 *
 * NOT the whole `RunState`. What a finished turn has to be able to say is: did it end, how, and
 * what went wrong — so this is the terminal status, the outcome, the gaps flag, and the run-WIDE
 * errors (`routeErrors(...).run`; anchored ones render at the thing that failed and are not the
 * transcript's to repeat). Everything else about a past run is already in its messages.
 */
export interface ArchivedOutcome {
  status: RunStatus;
  outcome: Outcome | null;
  completedWithGaps: boolean;
  errors: ProtocolError[];
}

/**
 * The archive's copy of how the live run is going, taken while it is still live.
 *
 * Called on every frame by `WeaveTranscript` (the same place and the same reason as
 * `archivedChangesetOf`): the store keeps one run, so a run's ending has to be copied out BEFORE
 * the next run replaces it or it is lost. Returns `null` while there is nothing worth keeping — a
 * healthy run that has not finished yet — so an in-flight turn carries no stale ending.
 */
export function archivedOutcomeOf(
  /*
   * Typed by the three fields it READS, not as a whole `RunState["run"]`, so both callers can pass
   * what they actually have without a cast: the live transcript passes the run object, and
   * `sessionHistory` passes the two fields `session_runs[]` carries. A cast at either call site
   * would be claiming a shape that is not there.
   */
  run: { status: string; outcome?: Outcome | null; completed_with_gaps?: boolean } | null | undefined,
  errors: readonly ProtocolError[],
): ArchivedOutcome | null {
  if (!run) return null;
  const terminal = TERMINAL_RUN_STATUSES.has(run.status);
  // Errors are kept even mid-run: a run that failed and was superseded before it reached a terminal
  // frame still owes the reader the reason it stopped.
  if (!terminal && !errors.length) return null;
  return {
    status: run.status as RunStatus,
    outcome: run.outcome ?? null,
    completedWithGaps: run.completed_with_gaps === true,
    errors: [...errors],
  };
}

/**
 * The closing sentence for a run that is no longer live, from the SAME table the live one uses.
 *
 * `closingLine` reads exactly three fields off the run; an `ArchivedOutcome` is those three fields.
 * Widening `closingLine`'s parameter would let any partial run object in, so the narrowing is done
 * here instead, once, where both shapes are already known — the alternative is a second copy of the
 * outcome→sentence table, which is how two surfaces start disagreeing about what a failure is
 * called.
 */
export function outcomeClosingLine(
  outcome: ArchivedOutcome | null | undefined,
): { text: string; tone: "ok" | "warn" | "bad" } | null {
  if (!outcome) return null;
  return closingLine({
    status: outcome.status,
    outcome: outcome.outcome,
    completed_with_gaps: outcome.completedWithGaps,
  } as RunState["run"]);
}

/** How a past run ENDED goes on its LAST turn — under the answer that stopped there. */
function withOutcome(turns: Turn[], outcome: ArchivedOutcome | null): Turn[] {
  if (!outcome || !turns.length) return turns;
  return turns.map((turn, index) => (index === turns.length - 1 ? { ...turn, outcome } : turn));
}

/** The changeset card of a past run goes on its LAST turn — under the answer that produced it. */
function withReferences(turns: Turn[], changeset: ArchivedChangeset | null): Turn[] {
  if (!changeset || !turns.length) return turns;
  const card: DerivedReference = {
    target: "changeset",
    title: "Changes",
    subtitle: changeset.subtitle,
    cta: CTA.changeset,
    tone: "quiet",
    // Optional on the archive: entries persisted before previews existed have none, and the
    // card renders as the plain opener it always was.
    ...(changeset.previews?.length
      ? { previews: changeset.previews, previewsHidden: changeset.previewsHidden ?? 0 }
      : {}),
  };
  return turns.map((turn, index) => (index === turns.length - 1 ? { ...turn, references: [card] } : turn));
}

/**
 * What the archive keeps of a run's changeset: enough to card it, and to recognise it again.
 *
 * NOT the changeset itself. `components[]` can be large and the archive is written on every frame;
 * the card needs only its subtitle, and the carried-changeset check (`isCarriedChangeset`) needs
 * only the identity — `changeset_id` plus `version`, the backend's own monotonic counter.
 */
export interface ArchivedChangeset {
  changesetId: string;
  version: number;
  subtitle: string | null;
  /**
   * The first lines of the files this run wrote — `changePreviews`, at most a few files and a few
   * lines each, so the code a turn produced stays visible under THAT turn after a follow-up has
   * made it history. Small by construction: the archive is written on every frame and persisted.
   * Optional because entries written before previews existed have none.
   */
  previews?: ChangePreview[];
  previewsHidden?: number;
}

export function archivedChangesetOf(changeset: Changeset | null | undefined): ArchivedChangeset | null {
  if (!changeset?.components?.length) return null;
  const { previews, hidden } = changePreviews(changeset);
  return {
    changesetId: changeset.changeset_id,
    version: typeof changeset.version === "number" ? changeset.version : 0,
    subtitle: subtitleFor("changeset", { changeset }),
    ...(previews.length ? { previews, previewsHidden: hidden } : {}),
  };
}

// ── The produced code, in the conversation ──────────────────────────────────────────────────────

/** Lines of a changed file the conversation shows before the reader opens the diff. */
export const CHANGE_PREVIEW_LINES = 8;
/** Files previewed under the changes card; the rest are counted and the pane has them all. */
export const CHANGE_PREVIEW_FILES = 3;

/**
 * A changed file, as the conversation previews it.
 *
 * `fileId` is what the click sends: `ChangesetPane` seeds its selection from a `file_id`
 * (`resolveFocusFile`), so opening the preview lands the reader on THIS file's diff rather than on
 * the board's first row. `lines` is the excerpt; `lineCount` is the whole file, so the footer can
 * say honestly how much more there is.
 */
export interface ChangePreview {
  fileId: string;
  componentId: string;
  /** The repo-relative path the backend sent. Never reconstructed. */
  path: string;
  /** The last path segment — what the header shows. */
  name: string;
  language: string;
  status: "created" | "modified" | "deleted";
  lineCount: number;
  lines: string[];
}

/**
 * The first lines of the files a changeset wrote, for the card in the conversation.
 *
 * ── WHY THE CHAT CAN SHOW CODE WITHOUT FETCHING ANYTHING ────────────────────────────────────────
 *
 * `Component.files[].after` (and `before`) cross the wire whole on `changeset.updated` and in every
 * `state.snapshot` (`graph/answer_views.py::files_from_node`), so the bytes are already in the
 * reducer's `changeset`. A preview that re-requested them would give the same file two sources.
 *
 * ── WHAT IS SHOWN, AND WHAT IS NOT ──────────────────────────────────────────────────────────────
 *
 *   - `unchanged` files: nothing changed, nothing to preview.
 *   - `role: "meta"` sidecars (`-meta.xml`): generated alongside the source and never hand-read;
 *     the board still lists them, the conversation does not lead with them.
 *   - a file with no body to show (a `created` file whose `after` is still null, an unresolved
 *     base): skipped rather than rendered empty. `base_unknown` is the board's problem to explain.
 *   - a `deleted` file previews what is being removed — `before` — because that is the decision.
 *
 * Capped at `files` previews, oldest component first (the order the backend emitted them). The
 * count of what was not shown comes back so the card can say "+N more files" instead of implying
 * that three files were the whole change.
 */
export function changePreviews(
  changeset: Changeset | null | undefined,
  { files = CHANGE_PREVIEW_FILES, lines = CHANGE_PREVIEW_LINES }: { files?: number; lines?: number } = {},
): { previews: ChangePreview[]; hidden: number } {
  const previews: ChangePreview[] = [];
  let eligible = 0;
  for (const component of changeset?.components ?? []) {
    for (const file of (component.files ?? []) as ChangesetFile[]) {
      if (!file || file.status === "unchanged" || file.role === "meta") continue;
      const body = file.status === "deleted" ? file.before : (file.after ?? file.before);
      if (typeof body !== "string" || !body.trim()) continue;
      eligible += 1;
      if (previews.length >= files) continue;
      const all = body.trimEnd().split("\n");
      const path = typeof file.path === "string" ? file.path : "";
      previews.push({
        fileId: file.file_id,
        componentId: component.component_id,
        path,
        name: path.split("/").filter(Boolean).pop() ?? path,
        language: typeof file.language === "string" ? file.language : "",
        status: file.status,
        lineCount: all.length,
        lines: all.slice(0, lines),
      });
    }
  }
  return { previews, hidden: Math.max(0, eligible - previews.length) };
}

/**
 * Did this run merely INHERIT the changeset, rather than produce or change it?
 *
 * THE BUG THIS ANSWERS. "I asked for a plan, got the answer and the changeset; then I asked another
 * question and the changeset came first." The changeset is SESSION-scoped, and the backend hands a
 * follow-up run the session's changeset in its very first `state.snapshot` when the run made none
 * of its own (`gui_protocol/snapshot.py::_carry_session_changeset`). So from the store's point of
 * view the new run had a changeset before it had said a word — and a card derived from state
 * rendered it under the new question, while the turn that actually built it lost its card the
 * moment it stopped being the live run. Not a leak between sessions; the state is correct, and it
 * was being attributed to the wrong turn.
 *
 * The archive is what tells the two apart: an EARLIER run recorded the same `changeset_id` at the
 * same `version`, so nothing about it is this run's doing. The moment this run changes it, the
 * version moves and the card is rightly this run's — and the earlier turn keeps its own card, as of
 * the version it produced.
 */
export function isCarriedChangeset(
  changeset: Changeset | null | undefined,
  archived: readonly { runId: string; changeset?: ArchivedChangeset | null }[],
  runId: string | null,
): boolean {
  if (!changeset) return false;
  const version = typeof changeset.version === "number" ? changeset.version : 0;
  return archived.some(
    (entry) =>
      entry.runId !== runId &&
      entry.changeset?.changesetId === changeset.changeset_id &&
      entry.changeset.version === version,
  );
}

/** Stamp a run's mode on its first turn — the one with the reader's question. */
function withMode(turns: Turn[], mode: Mode | null): Turn[] {
  if (!mode || !turns.length) return turns;
  return turns.map((turn, index) => (index === 0 ? { ...turn, mode } : turn));
}
